import { useEffect, useState } from "react";
import { Card, CardBody, CardHeader, Col, Row, Button } from "reactstrap";
import LineEditorTables from "./lineEditorTables";
import QuotePreview from "./quotePreview";
import QuoteContent from "./quoteContent";
import { useQuoteRequestData } from "../../../hooks/useQuoteRequestData";

const QuoteEditor = ({
    quoteRevision,
    quoteItems, quoteCustomisation,
    onUpfrontGroupMoved, onUpfrontItemMoved, onAddUpfrontGroup, onDeleteUpfrontGroup, onUpfrontGroupExpandedChanged,
    onRecurringGroupMoved, onRecurringItemMoved, onAddRecurringGroup, onDeleteRecurringGroup, onRecurringGroupExpandedChanged,
    onEditRecurringGroup, onEditUpfrontGroup, onUpdateRecurringVisibleGroupOrItem, onUpdateUpfrontVisibleGroupOrItem,
    onUpdateQuoteTemplateId, onUpdateQuoteAdditionalFields, onUpdateUpfrontShowChildren, onUpdateRecurringShowChildren
}) => {
    const [currentActiveMainTab, setCurrentActiveMainTab] = useState("Line Editor");
    const [selectedTemplate, setSelectedTemplate] = useState(null);
    const { salesForceData, customContent, exportData, upfrontItems, recurringItems, isLoadingOpportunity, isLoadingTemplates, templatesData } =
        useQuoteRequestData(quoteRevision, quoteItems, quoteCustomisation, selectedTemplate);

    useEffect(() => {
        if (templatesData?.length && quoteRevision?.quoteTemplateId) {
            const templateId = quoteRevision?.quoteTemplateId;
            if (!templateId || !templatesData?.length) return;

            const foundTemplate = templatesData.find(t => t.id === templateId) ?? null;
            if (!foundTemplate) return;
            foundTemplate.QuoteTemplateTags = [];
            setSelectedTemplate(foundTemplate);
        }

    }, [quoteRevision?.quoteTemplateId, templatesData]);

    // --- derived loading & readiness flags ---
    const isAnyLoading =
        isLoadingTemplates ||
        isLoadingOpportunity; // (will be false when not enabled)

    // const hasQuoteItems = Array.isArray(quoteItems) && quoteItems.length > 0;
    // const isTemplateReady = !!selectedTemplate;
    // const exportHasData =
    //     !!exportData &&
    //     Array.isArray(exportData.upfront) &&
    //     Array.isArray(exportData.recurring) &&
    //     (exportData.upfront.length > 0 || exportData.recurring.length > 0);

    // // Only show the preview when everything we need is present
    // const previewReady =
    //     !isAnyLoading &&
    //     !quoteRevisionError &&
    //     !templatesError &&
    //     !opportunityError &&
    //     isTemplateReady &&
    //     hasQuoteItems &&
    //     exportHasData;

    return (
        <>
            <Row>
                <Col>
                    <Card style={{ height: "100%" }}>
                        <CardHeader>
                            <Row>
                                <Col style={{ display: "inline-flex" }}>
                                    {["Line Editor", /*"Line Editor (NestedDataTable)"*/, "Template"].map((tab) => (
                                        <h6
                                            key={tab}
                                            className={
                                                currentActiveMainTab === tab
                                                    ? "custom-nav-selected custom-nav m-3"
                                                    : "custom-nav m-3"
                                            }
                                            style={{ cursor: "pointer" }}
                                            onClick={() => setCurrentActiveMainTab(tab)}
                                        >
                                            {tab}
                                        </h6>
                                    ))}
                                </Col>
                            </Row>
                        </CardHeader>
                        <CardBody>
                            {currentActiveMainTab === "Line Editor" ? (
                                <LineEditorTables
                                    upfrontGroups={quoteCustomisation.groups.upfront}
                                    recurringGroups={quoteCustomisation.groups.recurring}
                                    upfrontItems={upfrontItems}
                                    recurringItems={recurringItems}
                                    groups={quoteCustomisation.groups ?? { upfront: [], recurring: [] }}
                                    onUpfrontGroupMoved={onUpfrontGroupMoved}
                                    onUpfrontItemMoved={onUpfrontItemMoved}
                                    onAddUpfrontGroup={onAddUpfrontGroup}
                                    onDeleteUpfrontGroup={onDeleteUpfrontGroup}
                                    onRecurringGroupMoved={onRecurringGroupMoved}
                                    onRecurringItemMoved={onRecurringItemMoved}
                                    onAddRecurringGroup={onAddRecurringGroup}
                                    onDeleteRecurringGroup={onDeleteRecurringGroup}
                                    onUpfrontGroupExpandedChanged={onUpfrontGroupExpandedChanged}
                                    onRecurringGroupExpandedChanged={onRecurringGroupExpandedChanged}
                                    onEditRecurringGroup={onEditRecurringGroup}
                                    onEditUpfrontGroup={onEditUpfrontGroup}
                                    onUpdateRecurringVisibleGroupOrItem={onUpdateRecurringVisibleGroupOrItem}
                                    onUpdateUpfrontVisibleGroupOrItem={onUpdateUpfrontVisibleGroupOrItem}
                                    onUpdateUpfrontShowChildren={onUpdateUpfrontShowChildren}
                                    onUpdateRecurringShowChildren={onUpdateRecurringShowChildren}
                                    mode="flat"
                                />
                            ) : currentActiveMainTab === "Line Editor (NestedDataTable)" ? (
                                <LineEditorTables
                                    quoteItems={quoteItems}
                                    groups={quoteCustomisation.groups ?? { upfront: [], recurring: [] }}
                                    onUpfrontGroupMoved={onUpfrontGroupMoved}
                                    onUpfrontItemMoved={onUpfrontItemMoved}
                                    onAddUpfrontGroup={onAddUpfrontGroup}
                                    onDeleteUpfrontGroup={onDeleteUpfrontGroup}
                                    onRecurringGroupMoved={onRecurringGroupMoved}
                                    onRecurringItemMoved={onRecurringItemMoved}
                                    onAddRecurringGroup={onAddRecurringGroup}
                                    onDeleteRecurringGroup={onDeleteRecurringGroup}
                                    onUpfrontGroupExpandedChanged={onUpfrontGroupExpandedChanged}
                                    onRecurringGroupExpandedChanged={onRecurringGroupExpandedChanged}
                                    mode="nested"
                                />
                            ) : currentActiveMainTab === "Template" ? (
                                <div>
                                    <QuoteContent
                                        quoteRevision={quoteRevision}
                                        templatesData={templatesData}
                                        selectedTemplate={selectedTemplate}
                                        setSelectedTemplate={setSelectedTemplate}
                                        onUpdateQuoteTemplateId={onUpdateQuoteTemplateId}
                                        onUpdateQuoteAdditionalFields={onUpdateQuoteAdditionalFields}
                                        customContent={customContent}
                                    />
                                </div>
                            ) : (
                                <div>
                                    Placeholder for content editor.
                                </div>
                            )}
                        </CardBody>
                    </Card>
                </Col>
                <Col>
                    {isAnyLoading ? (
                        <div style={{ width: "100%", height: "80vh", minHeight: "400px" }}>
                            <div className="h-100 d-flex align-items-center justify-content-center flex-column text-center">
                                <div className="spinner-border text-primary" role="status"></div>
                                <p className="mt-3 mb-0">Loading…</p>
                            </div>
                        </div>
                    ) : (
                        <QuotePreview
                            quoteItems={quoteItems}
                            groupedQuoteItems={exportData}
                            salesForceData={salesForceData}
                            customContent={customContent}
                            template={selectedTemplate}
                        />
                    )}

                </Col>
            </Row>
        </>

    );
}
export default QuoteEditor;